import DataTable from '../components/DataTable';
import BarChart from '../components/BarChart';
import LineChart from '../components/LineChart';
import FleetStyleKpiCard from '../components/FleetStyleKpiCard';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';

/** Categorical (tab10 gray) for the neutral "Baseline" series; "AI-driven" carries status meaning. */
const BASELINE_COLOR = CHART_PALETTE[7];
const AI_DRIVEN_COLOR = STATUS_COLORS.success;

export default function AIDispatch() {
  const iterationRows = [
    {
      iteration: 'Run 1',
      baselineResolution: '89.2%',
      assistedResolution: '94.7%',
      baselineResponse: '15.3 min',
      assistedResponse: '11.8 min',
      baselineFuel: '1,450 L',
      assistedFuel: '1,280 L',
      baselineEnergy: '0 kWh',
      assistedEnergy: '450 kWh',
    },
    {
      iteration: 'Run 2',
      baselineResolution: '91.5%',
      assistedResolution: '96.2%',
      baselineResponse: '14.8 min',
      assistedResponse: '11.2 min',
      baselineFuel: '1,390 L',
      assistedFuel: '1,210 L',
      baselineEnergy: '0 kWh',
      assistedEnergy: '480 kWh',
    },
    {
      iteration: 'Run 3',
      baselineResolution: '90.8%',
      assistedResolution: '95.9%',
      baselineResponse: '15.1 min',
      assistedResponse: '11.5 min',
      baselineFuel: '1,420 L',
      assistedFuel: '1,240 L',
      baselineEnergy: '0 kWh',
      assistedEnergy: '465 kWh',
    },
  ];

  const categoryRows = [
    {
      category: 'Structure fire',
      incidents: 42,
      baselineAvgArrival: '8.4 min',
      assistedAvgArrival: '6.9 min',
      baselineResolvedPct: '88%',
      assistedResolvedPct: '94%',
    },
    {
      category: 'RTC / rescue',
      incidents: 58,
      baselineAvgArrival: '11.2 min',
      assistedAvgArrival: '9.1 min',
      baselineResolvedPct: '91%',
      assistedResolvedPct: '96%',
    },
    {
      category: 'Alarm / investigation',
      incidents: 103,
      baselineAvgArrival: '14.6 min',
      assistedAvgArrival: '12.4 min',
      baselineResolvedPct: '86%',
      assistedResolvedPct: '92%',
    },
    {
      category: 'Other',
      incidents: 27,
      baselineAvgArrival: '12.8 min',
      assistedAvgArrival: '10.6 min',
      baselineResolvedPct: '84%',
      assistedResolvedPct: '90%',
    },
  ];

  /** BarChart: each group = metric; "vehicle" field = scenario label */
  const summaryBarData = [
    {
      label: 'Resolution rate (%)',
      values: [
        { vehicle: 'Baseline', value: 90.5, color: BASELINE_COLOR },
        { vehicle: 'AI-driven', value: 95.6, color: AI_DRIVEN_COLOR },
      ],
    },
    {
      label: 'Avg. response (min)',
      values: [
        { vehicle: 'Baseline', value: 15.1, color: BASELINE_COLOR },
        { vehicle: 'AI-driven', value: 11.5, color: AI_DRIVEN_COLOR },
      ],
    },
    {
      label: 'Fuel (index)',
      values: [
        { vehicle: 'Baseline', value: 100, color: BASELINE_COLOR },
        { vehicle: 'AI-driven', value: 87, color: AI_DRIVEN_COLOR },
      ],
    },
    {
      label: 'Grid energy (kWh)',
      values: [
        { vehicle: 'Baseline', value: 0, color: BASELINE_COLOR },
        { vehicle: 'AI-driven', value: 465, color: AI_DRIVEN_COLOR },
      ],
    },
  ];

  const resolutionTrendSeries = [
    {
      name: 'Baseline',
      color: BASELINE_COLOR,
      data: [
        { label: 'Week 1', value: 88 },
        { label: 'Week 2', value: 90 },
        { label: 'Week 3', value: 89 },
        { label: 'Week 4', value: 91 },
      ],
    },
    {
      name: 'AI-driven',
      color: AI_DRIVEN_COLOR,
      data: [
        { label: 'Week 1', value: 93 },
        { label: 'Week 2', value: 95 },
        { label: 'Week 3', value: 94 },
        { label: 'Week 4', value: 96 },
      ],
    },
  ];

  return (
    <div className="space-y-8 pb-10">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-white mb-2">AI Dispatch</h2>
        <p className="text-muted-foreground text-sm max-w-3xl">
          Compare baseline dispatch rules with an AI-driven planner on the same incident set and demand profile. Use the summary
          below to review resolution, response, fuel, and energy outcomes side by side.
        </p>
      </div>

      <section>
        <h3 className="text-lg font-semibold text-white mb-1">Headline comparison</h3>
        <p className="text-muted-foreground text-sm mb-4">
          Uplift vs baseline across the last three runs — same incidents, different assignment policy.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <FleetStyleKpiCard
            title="Resolution rate"
            value="+5.1%"
            subtitle="Share of incidents closed within target window"
            accent="green"
          />
          <FleetStyleKpiCard
            title="Response time"
            value="−23%"
            subtitle="Mean time to first appliance on scene"
            accent="blue"
          />
          <FleetStyleKpiCard
            title="Diesel use"
            value="−13%"
            subtitle="Fleet fuel vs baseline (same demand)"
            accent="amber"
          />
          <FleetStyleKpiCard
            title="EV / grid use"
            value="+465 kWh"
            subtitle="Charging & traction energy (where applicable)"
            accent="purple"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 mb-8 lg:items-start">
          <div className="bg-card/50 rounded-xl border border-border p-4 min-h-0 overflow-visible flex flex-col">
            <h4 className="text-base font-semibold text-white mb-1">Scenario averages</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Baseline vs AI-driven — resolution %, time, fuel index, energy.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: BASELINE_COLOR }} />
                Baseline
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: AI_DRIVEN_COLOR }} />
                AI-driven
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 min-h-0">
              {summaryBarData.map((metric) => (
                <div
                  key={metric.label}
                  className="bg-background/50 rounded-lg border border-border/50 min-h-0 overflow-hidden shrink-0"
                >
                  <BarChart
                    data={[metric]}
                    title={metric.label}
                    height="min-h-0"
                    legendTitle="Scenario"
                    compact
                    hideLegend
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-card/50 rounded-xl border border-border p-4 min-h-0 flex flex-col">
            <h4 className="text-base font-semibold text-white mb-1">Resolution rate trend</h4>
            <p className="text-xs text-muted-foreground mb-2">Weekly resolution rate (%) by scenario.</p>
            <div className="w-full min-w-0 min-h-0">
              <LineChart
                series={resolutionTrendSeries}
                title=""
                dense
                heightClass="min-h-0"
                chartHeight={188}
                yAxisLabel="Resolution %"
                xAxisLabel="Period"
                hideDataPoints={false}
              />
            </div>
          </div>
        </div>

        <DataTable
          title="Run-by-run comparison"
          columns={[
            { key: 'iteration', label: 'Run' },
            { key: 'baselineResolution', label: 'Baseline resolution', align: 'right' as const },
            { key: 'assistedResolution', label: 'AI-driven resolution', align: 'right' as const },
            { key: 'baselineResponse', label: 'Baseline response time', align: 'right' as const },
            { key: 'assistedResponse', label: 'AI-driven response time', align: 'right' as const },
            { key: 'baselineFuel', label: 'Baseline fuel', align: 'right' as const },
            { key: 'assistedFuel', label: 'AI-driven fuel', align: 'right' as const },
            { key: 'baselineEnergy', label: 'Baseline energy', align: 'right' as const },
            { key: 'assistedEnergy', label: 'AI-driven energy', align: 'right' as const },
          ]}
          data={iterationRows}
          hideExport
          maxRows={6}
          hoverColor="green"
        />

        <div className="mt-8">
          <DataTable
            title="Outcomes by incident category"
            columns={[
              { key: 'category', label: 'Category' },
              { key: 'incidents', label: 'Incidents', align: 'right' as const },
              { key: 'baselineAvgArrival', label: 'Baseline avg. arrival', align: 'right' as const },
              { key: 'assistedAvgArrival', label: 'AI-driven avg. arrival', align: 'right' as const },
              { key: 'baselineResolvedPct', label: 'Baseline resolved', align: 'right' as const },
              { key: 'assistedResolvedPct', label: 'AI-driven resolved', align: 'right' as const },
            ]}
            data={categoryRows}
            hideExport
            maxRows={8}
            hoverColor="green"
          />
        </div>
      </section>
    </div>
  );
}
